import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import { useAuth } from '../context/AuthContext';
import { isAvailable, isFieldUsable } from '../lib/assetStatus';
import ChecklistRenderer, { flattenItems, initialValuesFor } from '../components/ChecklistRenderer';
import PhotoLightbox from '../components/PhotoLightbox';
import JobSelectionCard from '../components/JobSelectionCard';
import { uploadAssetChecklistPhoto } from '../lib/assetChecklistUploads';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Loader2,
  Truck,
  QrCode,
  LogIn,
  LogOut,
  History,
  Camera,
  X,
  Package,
  ArrowLeft,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatDateTimeET } from '../lib/dateHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status badge colors
const STATUS_COLORS = {
  'Active': 'bg-green-100 text-green-800 border-green-200',
  'Available': 'bg-green-100 text-green-800 border-green-200',  // legacy alias
  'Checked Out': 'bg-amber-100 text-amber-800 border-amber-200',
  'In Service': 'bg-blue-100 text-blue-800 border-blue-200',
  'Retired': 'bg-slate-100 text-slate-600 border-slate-200'
};

export default function AssetDetail() {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Dialogs
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [showCheckinDialog, setShowCheckinDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  
  const [assetHistory, setAssetHistory] = useState([]);
  const [checklistItems, setChecklistItems] = useState([]);
  const [checklistValues, setChecklistValues] = useState({});
  const [checklistTitle, setChecklistTitle] = useState('');
  // 'template' | 'placeholder' | 'none' — surfaced as a small badge in
  // each dialog so field staff know whether they're on a real linked
  // form or the fallback.
  const [checklistSource, setChecklistSource] = useState('placeholder');
  // Round 16 (2026-09-04) — verification marker: shows techs/admins
  // exactly when the live template content last changed, so "did my
  // wording edit actually reach the field" can be checked by looking
  // at the screen instead of trusting a DB query claim.
  const [checklistUpdatedAt, setChecklistUpdatedAt] = useState(null);
  const [showPretripDialog, setShowPretripDialog] = useState(false);
  
  // Signature
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  // Live data sources for the dynamic-option `select` items inside the
  // seeded checklists (e.g., "Employee Performing Inspection", "Select
  // Truck Being Inspected"). Fetched once when the page mounts.
  const [teamMembers, setTeamMembers] = useState([]);
  const [assetsForSelect, setAssetsForSelect] = useState([]);
  const [equipmentCategories, setEquipmentCategories] = useState([]);
  useEffect(() => {
    let mounted = true;
    Promise.all([
      axios.get(`${API}/team`).catch(() => ({ data: [] })),
      axios.get(`${API}/assets`).catch(() => ({ data: [] })),
      axios.get(`${API}/assets/equipment-categories`).catch(() => ({ data: [] })),
    ]).then(([tm, as, cats]) => {
      if (!mounted) return;
      setTeamMembers(Array.isArray(tm.data) ? tm.data : []);
      const equipmentCategories = Array.isArray(cats.data) ? cats.data : [];
      setEquipmentCategories(equipmentCategories);
      // Keep only rows with a real `name` (the assets list can contain
      // legacy test-marker rows without one) AND respect the
      // Check-Out/Check-In "Available" flag (asset-level `field_use`
      // or category-level, e.g. Shop Tools) — same rule the Check-Out
      // picker itself uses, so a truck like TV11 hidden there is also
      // hidden from this checklist dropdown.
      setAssetsForSelect(
        (Array.isArray(as.data) ? as.data : [])
          .filter(a => a && a.name)
          .filter(a => isFieldUsable(a, equipmentCategories))
      );
    });
    return () => { mounted = false; };
  }, []);

  // Feb-2026 Phase 2: Job Selection at the top of every equipment
  // checklist form (Check-Out, Check-In, Pre-Trip). Shared between
  // all three dialogs — the user picks a job once per dialog session
  // and the fields are submitted alongside the checklist payload.
  const [availableJobs, setAvailableJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState('');
  // "Your Jobs Today" — same source/logic as Time Tracker v2 and
  // AssetManagement.jsx (GET /time-blocks/assigned-jobs).
  const [myJobIds, setMyJobIds] = useState([]);
  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/jobs/for-field-reports`)
      .then(r => { if (mounted) setAvailableJobs(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (mounted) setAvailableJobs([]); })
      .finally(() => { if (mounted) setLoadingJobs(false); });
    axios.get(`${API}/time-blocks/assigned-jobs?tz_offset=${new Date().getTimezoneOffset()}`)
      .then(r => { if (mounted) setMyJobIds((Array.isArray(r.data) ? r.data : []).map(j => j.id)); })
      .catch(() => { if (mounted) setMyJobIds([]); });
    return () => { mounted = false; };
  }, []);
  const selectedJob = availableJobs.find(j => j.id === selectedJobId) || null;
  const jobPayload = () => (selectedJob ? {
    job_id: selectedJob.id,
    job_number: selectedJob.job_number,
    job_service_address: selectedJob.service_address || '',
    job_date: selectedJob.date || '',
    job_assigned_team: selectedJob.assigned_team_names || [],
  } : {});
  // Reset the job selection each time any of the dialogs closes so a
  // stale pick doesn't leak into the next asset action.
  const resetJobSelection = () => setSelectedJobId('');
  
  // Photos
  const photoInputRefs = useRef({});
  const [uploadingPhoto, setUploadingPhoto] = useState(null);

  // Lightbox state — shared across Check-Out / Check-In / Pre-Trip
  // dialogs so any thumbnail can be tapped to see the full-size image.
  const [lightboxPhotos, setLightboxPhotos] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const openLightbox = (photos, idx) => {
    setLightboxPhotos(photos || []);
    setLightboxIndex(idx || 0);
    setLightboxOpen(true);
  };

  // Fetch asset with auth token
  const fetchAsset = useCallback(async () => {
    // Get token from localStorage as fallback (in case context not ready)
    const authToken = token || localStorage.getItem('fieldflow_token');
    
    if (!authToken) {
      // No token - redirect to login
      setError('Please log in to view asset details');
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/assets/${assetId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setAsset(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch asset:', err);
      if (err.response?.status === 401) {
        setError('Please log in to view asset details');
      } else {
        setError(err.response?.data?.detail || 'Asset not found');
      }
    } finally {
      setLoading(false);
    }
  }, [assetId, token]);

  useEffect(() => {
    // Wait a brief moment for auth context to initialize from localStorage
    const timer = setTimeout(() => {
      fetchAsset();
    }, 100);
    return () => clearTimeout(timer);
  }, [fetchAsset]);

  // #24 fix (2026-08-27): dialogs used to auto-open on every direct
  // navigation to this page (the "coming from QR scan" comment this
  // replaced was never actually gated on anything — no code path ever
  // appended `?action=` to the URL, including the QR codes themselves,
  // so this fired unconditionally and blocked Pre-Trip/History behind
  // an unwanted modal). Check-Out/Check-In are now ONLY opened via the
  // explicit `checkout-btn`/`checkin-btn` buttons below
  // (handleOpenCheckout/handleOpenCheckin), which do the identical
  // checklist load + dialog open.

  // Handle checklist change
  const handleChecklistChange = (index, field, value) => {
    setChecklistValues(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: value
      }
    }));
  };

  // Signature handlers
  const getSignatureData = () => {
    if (!signatureCanvasRef.current || !hasSignature) return null;
    return signatureCanvasRef.current.toDataURL('image/png');
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setHasSignature(false);
    }
  };

  const startDrawing = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    // The <canvas> has a fixed buffer size (width={400} height={150})
    // but is rendered `w-full`, so the visible CSS size can be 2-3x
    // bigger. Mouse coords are in CSS pixels — we must scale them
    // back into buffer coordinates or the ink lands offset from the
    // pointer. This is THE canonical HTML5 canvas gotcha; see
    // https://stackoverflow.com/q/17130395 for background.
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    if (clientX == null || clientY == null) return;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    if (clientX == null || clientY == null) return;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  // Photo handlers
  const handlePhotoUpload = async (index, file) => {
    if (!assetId) {
      toast.error('Asset not loaded yet');
      return;
    }
    setUploadingPhoto(index);
    try {
      // Compress before uploading — cameras hand us multi-MB originals
      // that would balloon PDFs and OneDrive quota. Same settings as
      // the PreConForm / LocateForm flows so PDFs render identically.
      const compressedBlob = await imageCompression(file, {
        maxSizeMB: 0.3,
        maxWidthOrHeight: 1280,
        initialQuality: 0.82,
        useWebWorker: true,
        fileType: 'image/jpeg',
      });
      const compressedFile = new File(
        [compressedBlob],
        file.name || 'photo.jpg',
        { type: compressedBlob.type || 'image/jpeg', lastModified: Date.now() },
      );
      const authToken = token || localStorage.getItem('fieldflow_token');
      const res = await uploadAssetChecklistPhoto(assetId, compressedFile, authToken);
      const url = res?.url;
      if (!url) {
        toast.error('Photo upload failed');
        return;
      }
      setChecklistValues(prev => ({
        ...prev,
        [index]: {
          ...prev[index],
          photos: [...(prev[index]?.photos || []), url],
        },
      }));
      toast.success('Photo added');
    } catch (err) {
      console.error('Photo upload failed:', err);
      toast.error('Photo upload failed');
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handleRemovePhoto = (checklistIndex, photoIndex) => {
    setChecklistValues(prev => ({
      ...prev,
      [checklistIndex]: {
        ...prev[checklistIndex],
        photos: prev[checklistIndex].photos.filter((_, i) => i !== photoIndex)
      }
    }));
  };

  // Open checkout
  const handleOpenCheckout = async () => {
    if (!user) {
      toast.error('Please log in to check out assets');
      navigate('/login');
      return;
    }
    // #28 fix (2026-08-27): reset any stale job selection from a
    // previous open+cancel of this same dialog — matches
    // AssetManagement.jsx's handleOpenCheckout, which already resets
    // on open rather than only on submit success.
    resetJobSelection();
    
    try {
      const url = asset.subcategory_id
        ? `${API}/assets/checkout-checklist-by-subcategory/${asset.subcategory_id}`
        : `${API}/assets/checkout-checklist/${asset.category}`;
      const response = await axios.get(url);
      // Response shape: { source, template_id, title, sections }
      const flat = flattenItems(response.data.sections);
      setChecklistItems(flat);
      setChecklistTitle(response.data.title || 'Check-Out Checklist');
      setChecklistSource(response.data.source);
      setChecklistUpdatedAt(response.data.updated_at || null);
      setChecklistValues(initialValuesFor(flat, {
        employeeName: user?.full_name,
        assetLabel: asset.asset_number ? `${asset.name} (${asset.asset_number})` : asset.name,
      }));
      setHasSignature(false);
      setShowCheckoutDialog(true);
    } catch (err) {
      toast.error('Failed to load checklist');
    }
  };

  const handleOpenCheckin = async () => {
    if (!user) {
      toast.error('Please log in to check in assets');
      navigate('/login');
      return;
    }
    // #28 fix (2026-08-27): same reset-on-open as handleOpenCheckout.
    resetJobSelection();

    try {
      // Prefer subcategory-scoped endpoint; fall back to the legacy
      // category endpoint (which just returns the placeholder now)
      // when the asset has no subcategory.
      const url = asset.subcategory_id
        ? `${API}/assets/checkin-checklist-by-subcategory/${asset.subcategory_id}`
        : `${API}/assets/checkin-checklist/${asset.category}`;
      const response = await axios.get(url);
      const flat = flattenItems(response.data.sections);
      setChecklistItems(flat);
      setChecklistTitle(response.data.title || 'Check-In Checklist');
      setChecklistSource(response.data.source);
      setChecklistUpdatedAt(response.data.updated_at || null);
      setChecklistValues(initialValuesFor(flat, {
        employeeName: user?.full_name,
        assetLabel: asset.asset_number ? `${asset.name} (${asset.asset_number})` : asset.name,
      }));
      setHasSignature(false);
      setShowCheckinDialog(true);
      // Link back to the originating check-out and pre-fill the Job it
      // went out on — falls back to blank/manual entry when there's no
      // last_checkout_id (brand-new asset, or a pre-migration record)
      // or the lookup fails.
      if (asset.last_checkout_id) {
        try {
          const txResp = await axios.get(`${API}/assets/transactions/${asset.last_checkout_id}`);
          if (txResp.data?.job_id) {
            setSelectedJobId(txResp.data.job_id);
          }
        } catch (e) {
          // No active checkout record found — leave Job blank for manual entry.
        }
      }
    } catch (err) {
      toast.error('Failed to load checklist');
    }
  };

  // Pre-Trip flow — no legacy placeholder fallback. If the category
  // has no checkout_template_id linked (pre-trip shares it), we show
  // a friendly "not configured" state rather than pretending everything's fine.
  const handleOpenPretrip = async () => {
    if (!user) {
      toast.error('Please log in to run a pre-trip inspection');
      navigate('/login');
      return;
    }
    try {
      const url = asset.subcategory_id
        ? `${API}/assets/pretrip-checklist-by-subcategory/${asset.subcategory_id}`
        : `${API}/assets/pretrip-checklist/${asset.category}`;
      const response = await axios.get(url);
      if (response.data.source === 'none') {
        const target = asset.subcategory_name
          ? `"${asset.subcategory_name}" subcategory`
          : `"${asset.category}" category`;
        toast.error(`No Pre-Trip template is linked to the ${target}. Ask an admin to set one in Settings → Equipment Categories.`);
        return;
      }
      const flat = flattenItems(response.data.sections);
      setChecklistItems(flat);
      setChecklistTitle(response.data.title || 'Pre-Trip Inspection');
      setChecklistSource(response.data.source);
      setChecklistUpdatedAt(response.data.updated_at || null);
      setChecklistValues(initialValuesFor(flat, {
        employeeName: user?.full_name,
        assetLabel: asset.asset_number ? `${asset.name} (${asset.asset_number})` : asset.name,
      }));
      setHasSignature(false);
      setShowPretripDialog(true);
    } catch (err) {
      toast.error('Failed to load pre-trip checklist');
    }
  };

  // Submit pre-trip — writes an asset_transactions row (type='pretrip')
  // for manager approval, same treatment as check-out/check-in. Does
  // not change the asset's checkout state (pre-trip is a safety log,
  // not a hand-off of the equipment).
  const handleSubmitPretrip = async () => {
    setSaving(true);
    try {
      const checklist = Object.entries(checklistValues).map(([, v]) => ({
        label: v.label,
        section: v.section_name,
        type: v.type,
        checked: v.checked,
        value: v.value,
        selections: v.selections,
        notes: v.notes,
        photos: v.photos,
        photo: v.photo,
      }));
      const signature = getSignatureData();
      await axios.post(`${API}/assets/${asset.id}/pretrip`, {
        checklist,
        signature,
        notes: '',
        ...jobPayload(),
      });
      toast.success('Pre-trip inspection submitted for manager review');
      setShowPretripDialog(false);
      resetJobSelection();
      fetchAsset();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit pre-trip');
    } finally {
      setSaving(false);
    }
  };

  // Submit checkout
  const handleSubmitCheckout = async () => {
    setSaving(true);
    try {
      const checklist = Object.values(checklistValues).map(v => ({
        label: v.label,
        section: v.section_name,
        type: v.type,
        checked: v.checked,
        value: v.value,
        selections: v.selections,
        notes: v.notes,
        photos: v.photos,
        photo: v.photo,
      }));

      const signature = getSignatureData();
      
      await axios.post(`${API}/assets/${asset.id}/checkout`, {
        checklist,
        signature,
        notes: '',
        ...jobPayload(),
      });
      
      toast.success('Asset checked out successfully');
      setShowCheckoutDialog(false);
      resetJobSelection();
      fetchAsset();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to check out asset');
    } finally {
      setSaving(false);
    }
  };

  // Submit checkin
  const handleSubmitCheckin = async () => {
    setSaving(true);
    try {
      const checklist = Object.values(checklistValues).map(v => ({
        label: v.label,
        section: v.section_name,
        type: v.type,
        checked: v.checked,
        value: v.value,
        selections: v.selections,
        notes: v.notes,
        photos: v.photos,
        photo: v.photo,
      }));

      const signature = getSignatureData();
      
      await axios.post(`${API}/assets/${asset.id}/checkin`, {
        checklist,
        signature,
        notes: '',
        ...jobPayload(),
      });
      
      toast.success('Asset checked in successfully');
      setShowCheckinDialog(false);
      resetJobSelection();
      fetchAsset();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to check in asset');
    } finally {
      setSaving(false);
    }
  };

  // View history
  const handleViewHistory = async () => {
    try {
      const response = await axios.get(`${API}/assets/${asset.id}/history`);
      setAssetHistory(response.data);
      setShowHistoryDialog(true);
    } catch (err) {
      toast.error('Failed to load history');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              {error.includes('log in') ? 'Login Required' : 'Asset Not Found'}
            </h2>
            <p className="text-slate-500 mb-4">{error}</p>
            {error.includes('log in') ? (
              <Button onClick={() => navigate(`/login?redirect=/asset/${assetId}`)} className="bg-amber-500 hover:bg-amber-400 text-slate-900">
                Log In
              </Button>
            ) : (
              <Button onClick={() => navigate('/maintenance')} variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4" data-testid="asset-detail-page">
      <div className="max-w-2xl mx-auto space-y-4">
        {/* Back button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/maintenance')}
          className="text-slate-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Assets
        </Button>

        {/* Asset Card */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Truck className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-lg font-['Barlow_Condensed']">{asset.name}</CardTitle>
                  <p className="text-amber-600 font-mono text-sm">{asset.asset_number}</p>
                </div>
              </div>
              <Badge className={`${STATUS_COLORS[asset.status]} border`}>
                {asset.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Asset Details */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Category</p>
                <p className="font-medium">{asset.category}</p>
              </div>
              {asset.subcategory_name && (
                <div data-testid="asset-detail-subcategory">
                  <p className="text-slate-500">Subcategory</p>
                  <p className="font-medium">{asset.subcategory_name}</p>
                </div>
              )}
              {asset.year && (
                <div>
                  <p className="text-slate-500">Year</p>
                  <p className="font-medium">{asset.year}</p>
                </div>
              )}
              {asset.make && (
                <div>
                  <p className="text-slate-500">Make</p>
                  <p className="font-medium">{asset.make}</p>
                </div>
              )}
              {asset.model && (
                <div>
                  <p className="text-slate-500">Model</p>
                  <p className="font-medium">{asset.model}</p>
                </div>
              )}
              {asset.vin && (
                <div className="col-span-2">
                  <p className="text-slate-500">VIN</p>
                  <p className="font-medium font-mono text-xs">{asset.vin}</p>
                </div>
              )}
              {asset.serial_number && (
                <div className="col-span-2">
                  <p className="text-slate-500">Serial Number</p>
                  <p className="font-medium font-mono text-xs">{asset.serial_number}</p>
                </div>
              )}
              {asset.license_plate && (
                <div>
                  <p className="text-slate-500">License Plate</p>
                  <p className="font-medium">{asset.license_plate}</p>
                </div>
              )}
            </div>

            {/* Current Holder Info */}
            {asset.status === 'Checked Out' && asset.current_holder && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <span className="font-medium">Currently checked out by:</span> {asset.current_holder}
                </p>
                {asset.checked_out_at && (
                  <p className="text-xs text-amber-600 mt-1">
                    Since {formatDateTimeET(asset.checked_out_at)}
                  </p>
                )}
                {user && asset.current_holder_id !== user.id && !['admin', 'manager', 'super_user'].includes(user.role) && (
                  <p className="text-xs text-amber-700 mt-2 font-medium">
                    Only {asset.current_holder} or a manager can check this asset back in.
                  </p>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              {!isFieldUsable(asset, equipmentCategories) ? (
                <p className="text-sm text-slate-500 italic" data-testid="no-checkout-note">
                  Maintenance-only equipment — no Check-Out/Check-In/Pre-Trip for this asset.
                </p>
              ) : (
              <>
              {isAvailable(asset) ? (
                <Button
                  onClick={handleOpenCheckout}
                  className="flex-1 bg-green-600 hover:bg-green-500 text-white"
                  data-testid="checkout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Check Out
                </Button>
              ) : asset.status === 'Checked Out' && user && (
                asset.current_holder_id === user.id || ['admin', 'manager', 'super_user'].includes(user.role)
              ) ? (
                <Button
                  onClick={handleOpenCheckin}
                  className="flex-1 bg-blue-600 hover:bg-blue-500 text-white"
                  data-testid="checkin-btn"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Check In
                </Button>
              ) : null}

              {/* Pre-Trip is orthogonal to check-out state: field
                  crews may run it before starting the day whether the
                  asset is Available or Checked Out to them. Button
                  itself is always visible when logged in; the
                  handler will toast a helpful message if no pre-trip
                  template is linked to this category. */}
              {user && (
                <Button
                  variant="outline"
                  onClick={handleOpenPretrip}
                  className="flex-1 border-amber-500 text-amber-700 hover:bg-amber-50"
                  data-testid="pretrip-btn"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Pre-Trip
                </Button>
              )}
              </>
              )}

              <Button
                variant="outline"
                onClick={handleViewHistory}
                className="flex-1"
              >
                <History className="w-4 h-4 mr-2" />
                View History
              </Button>
            </div>

            {/* Login prompt if not authenticated */}
            {!user && (
              <div className="p-3 bg-slate-100 border border-slate-200 rounded-lg text-center">
                <p className="text-sm text-slate-600 mb-2">
                  Log in to check out or check in this asset
                </p>
                <Button onClick={() => navigate('/login')} size="sm">
                  Log In
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Checkout Dialog */}
      <Dialog open={showCheckoutDialog} onOpenChange={setShowCheckoutDialog}>
        <DialogContent
          className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto"
          // Fix (2026-08-30): PhotoLightbox renders as its own overlay
          // outside this DialogContent's DOM subtree — Radix was
          // treating clicks on it as "outside" and closing (wiping)
          // this checkout form behind it. Ignore outside-interaction/
          // escape while the lightbox is open.
          onPointerDownOutside={(e) => { if (lightboxOpen) e.preventDefault(); }}
          onInteractOutside={(e) => { if (lightboxOpen) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (lightboxOpen) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-lg">
              Check Out: {asset?.name}
            </DialogTitle>
            <DialogDescription>
              Complete the inspection checklist before checking out this asset.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <JobSelectionCard
              value={selectedJobId}
              onChange={setSelectedJobId}
              availableJobs={availableJobs}
              loadingJobs={loadingJobs}
              selectedJob={selectedJob}
              myJobIds={myJobIds}
              testIdPrefix="checkout-job-selection"
              summaryVariant="detailed"
            />
            {/* Small source badge so field staff can see whether they're
                on a real linked template or the placeholder fallback. */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs uppercase font-semibold text-slate-500">Form:</span>
              <span className="text-sm font-medium text-slate-800">{checklistTitle}</span>
              <span className={`text-xs px-2 py-0.5 rounded ${checklistSource === 'template' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                {checklistSource === 'template' ? 'Linked template' : 'Fallback (no template linked)'}
              </span>
              {checklistSource === 'template' && checklistUpdatedAt && (
                <span className="text-xs text-slate-400" data-testid="checkout-checklist-updated-at">
                  Updated: {new Date(checklistUpdatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              )}
            </div>
            <ChecklistRenderer
              flatItems={checklistItems}
              values={checklistValues}
              onChange={handleChecklistChange}
              onPhotoUpload={(idx, file) => handlePhotoUpload(idx, file)}
              uploadingPhotoIdx={uploadingPhoto}
              onPhotoRemove={handleRemovePhoto}
              onPhotoClick={openLightbox}
              teamMembers={teamMembers}
              assetsForSelect={assetsForSelect}
              testIdPrefix="checkout"
            />

            <div className="space-y-2">
              <Label className="font-medium">Submitted By</Label>
              <div className="p-3 bg-slate-100 border border-slate-200 rounded-lg">
                <p className="font-medium text-slate-800">{user?.full_name}</p>
                <p className="text-sm text-slate-500">{user?.email}</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-medium">Signature</Label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-2 bg-white">
                <canvas
                  ref={signatureCanvasRef}
                  width={400}
                  height={150}
                  className="w-full border border-slate-200 rounded cursor-crosshair touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearSignature}
                  className="mt-2 text-slate-500"
                >
                  Clear Signature
                </Button>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowCheckoutDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmitCheckout}
                disabled={saving || !hasSignature || !selectedJobId}
                className="bg-green-600 hover:bg-green-500 text-white"
                data-testid="checkout-submit-btn"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Submit for Approval
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Checkin Dialog */}
      <Dialog open={showCheckinDialog} onOpenChange={setShowCheckinDialog}>
        <DialogContent
          className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto"
          // Fix (2026-08-30): same lightbox-outside-click guard as the
          // Checkout dialog above.
          onPointerDownOutside={(e) => { if (lightboxOpen) e.preventDefault(); }}
          onInteractOutside={(e) => { if (lightboxOpen) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (lightboxOpen) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-lg">
              Check In: {asset?.name}
            </DialogTitle>
            <DialogDescription>
              Complete the return inspection before checking in this asset.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <JobSelectionCard
              value={selectedJobId}
              onChange={setSelectedJobId}
              availableJobs={availableJobs}
              loadingJobs={loadingJobs}
              selectedJob={selectedJob}
              myJobIds={myJobIds}
              testIdPrefix="checkin-job-selection"
              summaryVariant="detailed"
            />
            {/* Originally-checked-out-by banner — parity with
                AssetManagement.jsx's Check-In dialog. Also the source of
                the Job pre-fill above, via asset.last_checkout_id looked
                up in handleOpenCheckin. */}
            {asset.current_holder && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="checkin-current-holder-banner">
                <p className="text-sm text-amber-800">
                  <span className="font-medium">Originally checked out by:</span>{' '}{asset.current_holder}
                  {asset.checked_out_at && (
                    <span className="ml-2 text-amber-600">
                      since {formatDateTimeET(asset.checked_out_at)}
                    </span>
                  )}
                </p>
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs uppercase font-semibold text-slate-500">Form:</span>
              <span className="text-sm font-medium text-slate-800">{checklistTitle}</span>
              <span className={`text-xs px-2 py-0.5 rounded ${checklistSource === 'template' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                {checklistSource === 'template' ? 'Linked template' : 'Fallback (no template linked)'}
              </span>
              {checklistSource === 'template' && checklistUpdatedAt && (
                <span className="text-xs text-slate-400" data-testid="checkin-checklist-updated-at">
                  Updated: {new Date(checklistUpdatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              )}
            </div>
            <ChecklistRenderer
              flatItems={checklistItems}
              values={checklistValues}
              onChange={handleChecklistChange}
              onPhotoUpload={(idx, file) => handlePhotoUpload(idx, file)}
              uploadingPhotoIdx={uploadingPhoto}
              onPhotoRemove={handleRemovePhoto}
              onPhotoClick={openLightbox}
              teamMembers={teamMembers}
              assetsForSelect={assetsForSelect}
              testIdPrefix="checkin"
            />

            <div className="space-y-2">
              <Label className="font-medium">Returned By</Label>
              <div className="p-3 bg-slate-100 border border-slate-200 rounded-lg">
                <p className="font-medium text-slate-800">{user?.full_name}</p>
                <p className="text-sm text-slate-500">{user?.email}</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-medium">Signature</Label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-2 bg-white">
                <canvas
                  ref={signatureCanvasRef}
                  width={400}
                  height={150}
                  className="w-full border border-slate-200 rounded cursor-crosshair touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearSignature}
                  className="mt-2 text-slate-500"
                >
                  Clear Signature
                </Button>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowCheckinDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmitCheckin}
                disabled={saving || !hasSignature || !selectedJobId}
                className="bg-blue-600 hover:bg-blue-500 text-white"
                data-testid="checkin-submit-btn"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Submit for Approval
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-lg">
              Asset History
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {assetHistory.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No history available</p>
            ) : (
              <div className="space-y-3">
                {assetHistory.map((record, idx) => (
                  <div key={idx} className={`p-3 rounded-lg border ${
                    record.type === 'checkout' 
                      ? 'bg-amber-50 border-amber-200' 
                      : 'bg-blue-50 border-blue-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {record.type === 'checkout' ? (
                          <LogOut className="w-4 h-4 text-amber-600" />
                        ) : (
                          <LogIn className="w-4 h-4 text-blue-600" />
                        )}
                        <span className="font-medium capitalize">
                          {record.type === 'checkout' ? 'Checked Out' : 'Checked In'}
                        </span>
                      </div>
                      <span className="text-xs text-slate-500">
                        {formatDateTimeET(record.created_at)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      By: {record.user_name}
                      {record.duration_hours && (
                        <span className="ml-2 text-slate-500">
                          (Duration: {record.duration_hours} hours)
                        </span>
                      )}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Pre-Trip Dialog — separate from Check Out / Check In because
          it doesn't transition asset state. Uses the same
          ChecklistRenderer as the other two so all typed items
          (yesno_na, signature, multiselect, ...) render consistently. */}
      <Dialog open={showPretripDialog} onOpenChange={setShowPretripDialog}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="pretrip-dialog"
          // Fix (2026-08-30): same lightbox-outside-click guard as the
          // Checkout/Checkin dialogs above.
          onPointerDownOutside={(e) => { if (lightboxOpen) e.preventDefault(); }}
          onInteractOutside={(e) => { if (lightboxOpen) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (lightboxOpen) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl">Pre-Trip Inspection</DialogTitle>
            <DialogDescription>Run through the pre-trip form for this asset. Nothing changes on the asset itself — this is a safety log.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <JobSelectionCard
              value={selectedJobId}
              onChange={setSelectedJobId}
              availableJobs={availableJobs}
              loadingJobs={loadingJobs}
              selectedJob={selectedJob}
              myJobIds={myJobIds}
              testIdPrefix="pretrip-job-selection"
              summaryVariant="detailed"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs uppercase font-semibold text-slate-500">Form:</span>
              <span className="text-sm font-medium text-slate-800">{checklistTitle}</span>
              <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">Linked template</span>
              {checklistUpdatedAt && (
                <span className="text-xs text-slate-400" data-testid="pretrip-checklist-updated-at">
                  Updated: {new Date(checklistUpdatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              )}
            </div>
            <ChecklistRenderer
              flatItems={checklistItems}
              values={checklistValues}
              onChange={handleChecklistChange}
              onPhotoUpload={(idx, file) => handlePhotoUpload(idx, file)}
              uploadingPhotoIdx={uploadingPhoto}
              onPhotoRemove={handleRemovePhoto}
              onPhotoClick={openLightbox}
              teamMembers={teamMembers}
              assetsForSelect={assetsForSelect}
              testIdPrefix="pretrip"
            />

            <div className="space-y-2">
              <Label className="font-medium">Submitted By</Label>
              <div className="p-3 bg-slate-100 border border-slate-200 rounded-lg">
                <p className="font-medium text-slate-800">{user?.full_name}</p>
                <p className="text-sm text-slate-500">{user?.email}</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-medium">Signature</Label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-2 bg-white">
                <canvas
                  ref={signatureCanvasRef}
                  width={400}
                  height={150}
                  className="w-full border border-slate-200 rounded cursor-crosshair touch-none"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  data-testid="pretrip-signature-canvas"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearSignature}
                  className="mt-2 text-slate-500"
                >
                  Clear Signature
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPretripDialog(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSubmitPretrip} disabled={saving || !hasSignature || !selectedJobId} className="bg-amber-500 hover:bg-amber-400 text-slate-900" data-testid="pretrip-submit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit Pre-Trip
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shared full-size photo viewer for all three flows. */}
      <PhotoLightbox
        open={lightboxOpen}
        photos={lightboxPhotos}
        index={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
        onNavigate={setLightboxIndex}
      />
    </div>
  );
}
